"use client";

import Script from "next/script";
import React, { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare let grecaptcha: Grecaptcha;

export interface GetFreeAssessmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  buttonLabel?: string;
  recipientEmail?: string;
}

const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";

export default function GetFreeAssessmentModal({
  open,
  onOpenChange,
  title = "Request a Consultation",
  buttonLabel = "Submit",
  recipientEmail,
}: GetFreeAssessmentModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    if (typeof grecaptcha === "undefined") {
      setErrorMessage("reCAPTCHA is not ready. Please try again later.");
      setLoading(false);
      return;
    }

    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(SITE_KEY, { action: "submit" });

        const body = new FormData();
        body.append("token", token);
        body.append("name", formData.name.trim());
        body.append("phone", formData.phone.trim());
        body.append("email", formData.email.trim());
        body.append("city", formData.city.trim());
        if (recipientEmail?.trim()) {
          body.append("recipientEmail", recipientEmail.trim());
        }

        const response = await fetch("/api/send-free-assessment", {
          method: "POST",
          body,
        });

        if (response.ok) {
          if (typeof window !== "undefined" && window.dataLayer) {
            window.dataLayer.push({
              event: "trichos_form_submit",
              formType: "freeAssessment",
              formId: "get-free-assessment-modal",
              formName: "Get Free Assessment (Modal)",
              submissionStatus: "success",
            });
          }
          onOpenChange(false);
          window.location.href = "/thank-you-consultation";
        } else {
          const data = await response.json();
          setErrorMessage(data.message ?? "Error submitting form.");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setErrorMessage("An error occurred. Please try again.");
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`}
        strategy="lazyOnload"
      />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md border-black/10 bg-white sm:rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-center text-xl font-bold text-black">
              {title}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={handleSubmit}
            id="get-free-assessment-modal-form"
            className="mt-4 space-y-4"
          >
            <div>
              <label htmlFor="modal-assessment-name" className="sr-only">
                Name
              </label>
              <input
                id="modal-assessment-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Name"
                className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-base text-black focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                autoComplete="name"
                aria-required
              />
            </div>
            <div>
              <label htmlFor="modal-assessment-phone" className="sr-only">
                Phone
              </label>
              <input
                id="modal-assessment-phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="Phone"
                className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-base text-black focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                autoComplete="tel"
                aria-required
              />
            </div>
            <div>
              <label htmlFor="modal-assessment-email" className="sr-only">
                Email
              </label>
              <input
                id="modal-assessment-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Email"
                className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-base text-black focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                autoComplete="email"
                aria-required
              />
            </div>
            <div>
              <label htmlFor="modal-assessment-city" className="sr-only">
                City
              </label>
              <input
                id="modal-assessment-city"
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                placeholder="City"
                className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-base text-black focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                autoComplete="address-level2"
                aria-required
              />
            </div>
            {errorMessage && (
              <p className="text-center text-sm text-red-600" role="alert">
                {errorMessage}
              </p>
            )}
            <div className="flex justify-center pt-2">
              <button
                type="submit"
                disabled={loading}
                className={`rounded-2xl bg-black px-8 py-3 text-base font-semibold text-white transition-colors ${
                  loading
                    ? "cursor-not-allowed bg-gray-400 text-gray-600"
                    : "hover:bg-neutral-900"
                }`}
                aria-busy={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="size-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Sending…
                  </span>
                ) : (
                  buttonLabel
                )}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
